import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/hooks/use-toast";
import Navbar from "@/components/Navbar";

const GsaCallForDestructionBounty = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedScenario, setSelectedScenario] = useState<any>(null);
  const [bountyAmount, setBountyAmount] = useState("2500");
  const [timeline, setTimeline] = useState("14");
  const [evidenceRequirements, setEvidenceRequirements] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);

  useEffect(() => {
    if (location.state?.selectedScenario) {
      setSelectedScenario(location.state.selectedScenario);
      // Pre-populate evidence requirements based on the scenario
      setEvidenceRequirements(`Evidence required for destruction of: ${location.state.selectedScenario.title}\n\nRequired documentation:\n- Current industry standards or regulations that supersede this content\n- Documented instances of confusion or errors caused by this content\n- Alternative resources that provide accurate information\n- Impact assessment of content removal\n\nValidation criteria:\n- Must demonstrate content is outdated, incorrect, or harmful\n- Must show newer standards or guidelines exist\n- Must provide replacement recommendations where applicable\n- Must comply with GSA records management policies`);
    }
  }, [location]);

  const handleSubmit = () => {
    if (!acceptTerms) {
      toast({
        title: "Terms Required",
        description: "Please accept the terms and conditions to proceed.",
        variant: "destructive"
      });
      return;
    }

    // Show success toast
    toast({
      title: "GSA Destruction Request Submitted Successfully",
      description: "Your destruction request has been posted to the GSA knowledge network and will appear in your mySPAI profile.",
    });
    
    // Navigate back to GSA Channel
    navigate("/gsa-channel");
  };

  if (!selectedScenario) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6">
            <p className="text-center text-gray-600">No scenario selected. Please go back and select a scenario.</p>
            <Button 
              onClick={() => navigate("/gsa-call-for-destruction")}
              className="w-full mt-4"
            >
              Back to Scenarios
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'Critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'High': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'Medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'High': return 'bg-red-100 text-red-800 border-red-200';
      case 'Medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-8">
          <img 
            src="/lovable-uploads/bb3f5ac9-6c4b-494d-9a60-86ee7a3feb77.png" 
            alt="iSPAI Logo" 
            className="h-16 mx-auto mb-6 cursor-pointer"
            onClick={() => navigate("/gsa-channel")}
          />
          <h1 className="text-3xl font-bold mb-4">Create GSA Destruction Bounty</h1>
        </div>

        {/* Selected Scenario Summary */}
        <Card className="mb-8 border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="text-xl text-red-800">Selected Destruction Target</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="mb-4">
              <h3 className="font-semibold text-red-900 mb-2">{selectedScenario.title}</h3>
              <p className="text-red-800 mb-4">{selectedScenario.description}</p>
              
              <div className="flex flex-wrap gap-2 mb-3">
                <span className={`px-3 py-1 text-sm rounded-full border ${getImpactColor(selectedScenario.impact)}`}>
                  Impact: {selectedScenario.impact}
                </span>
                <span className={`px-3 py-1 text-sm rounded-full border ${getUrgencyColor(selectedScenario.urgency)}`}>
                  Urgency: {selectedScenario.urgency}
                </span>
                <span className="px-3 py-1 bg-gray-100 text-gray-800 text-sm rounded-full border border-gray-200">
                  Type: {selectedScenario.contentType}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bounty Details Form */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Destruction Bounty Configuration</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="bountyAmount" className="text-base font-medium">Token Reward Amount</Label>
                <Input 
                  id="bountyAmount"
                  type="number"
                  value={bountyAmount}
                  onChange={(e) => setBountyAmount(e.target.value)}
                  className="mt-1"
                />
                <p className="text-sm text-gray-500 mt-1">Recommended: 1,000-5,000 tokens for destruction requests</p>
              </div>
              
              <div>
                <Label htmlFor="timeline" className="text-base font-medium">Review Timeline (Days)</Label>
                <Input 
                  id="timeline"
                  type="number"
                  value={timeline}
                  onChange={(e) => setTimeline(e.target.value)}
                  className="mt-1"
                />
                <p className="text-sm text-gray-500 mt-1">Typical GSA content review: 7-30 days</p>
              </div>
            </div>

            <div>
              <Label htmlFor="evidenceRequirements" className="text-base font-medium">Evidence Requirements</Label>
              <Textarea
                id="evidenceRequirements"
                value={evidenceRequirements}
                onChange={(e) => setEvidenceRequirements(e.target.value)}
                className="mt-1 min-h-32"
                placeholder="Specify required evidence and validation criteria..."
              />
            </div>
          </CardContent>
        </Card>

        {/* Federal Records Management Notice */}
        <Card className="mb-8 border-yellow-200 bg-yellow-50">
          <CardContent className="pt-6">
            <h3 className="text-lg font-semibold text-yellow-800 mb-2">Federal Records Management Compliance</h3>
            <p className="text-yellow-700 text-sm">
              This destruction request must comply with GSA records management policies and federal retention schedules. 
              All content removal will be properly documented and archived according to NARA guidelines before destruction.
            </p>
          </CardContent>
        </Card>

        {/* Terms and Conditions */}
        <div className="flex items-start space-x-2 mb-8">
          <Checkbox 
            id="terms" 
            checked={acceptTerms}
            onCheckedChange={(checked) => setAcceptTerms(checked as boolean)}
          />
          <div>
            <Label htmlFor="terms" className="cursor-pointer text-sm">
              I confirm this content should be removed and complies with GSA records policies
            </Label>
            <p className="text-gray-500 text-xs mt-1">
              By submitting, you confirm this content is outdated, incorrect, or harmful and that removal 
              aligns with federal records management requirements. Destruction will be reviewed by GSA knowledge curators.
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between">
          <Button 
            variant="outline" 
            onClick={() => navigate("/gsa-call-for-destruction")}
            className="border-gray-300"
          >
            Back to Scenarios
          </Button>
          
          <Button 
            onClick={handleSubmit}
            disabled={!acceptTerms}
            className="bg-[#14213d] hover:bg-[#0d1829] text-white px-8 py-2"
          >
            Submit Destruction Request
          </Button>
        </div>
      </main>
    </div>
  );
};

export default GsaCallForDestructionBounty;